import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft,
  ArrowRight,
  BriefcaseBusiness,
  Building2,
  Check,
  GraduationCap,
  MessageSquareText,
  Mic,
  Scale,
  Sparkles,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { buildStarterLaunchPath, buildStarterWorkflows, getPracticeTrackMeta, PRACTICE_TRACK_OPTIONS } from '@/lib/activation';
import { API } from '@/lib/api';

const TRACK_ICONS = {
  STUDENT: GraduationCap,
  SENIOR_ADVOCATE: Scale,
  AOR_CHAMBER_LEAD: BriefcaseBusiness,
  JUNIOR_CLERK: BriefcaseBusiness,
  INDEPENDENT_LITIGATOR: Scale,
  FIRM_LAWYER: Building2,
  CORPORATE_COUNSEL: Building2,
};

const PRACTICE_AREAS = ['Civil', 'Criminal', 'Constitutional', 'Commercial', 'Family', 'Property', 'Tax', 'Labour'];
const COURTS = ['Supreme Court', 'Delhi HC', 'Bombay HC', 'Madras HC', 'District Courts', 'Tribunals'];
const LANGUAGES = [
  { value: 'ENGLISH', label: 'English', code: 'EN' },
  { value: 'HINDI', label: 'Hindi', code: 'HI' },
  { value: 'TAMIL', label: 'Tamil', code: 'TA' },
  { value: 'TELUGU', label: 'Telugu', code: 'TE' },
  { value: 'MARATHI', label: 'Marathi', code: 'MR' },
  { value: 'BENGALI', label: 'Bengali', code: 'BN' },
];

function toggleItem(items, item) {
  return items.includes(item) ? items.filter((value) => value !== item) : [...items, item];
}

function getInstitutionLabel(track) {
  if (track === 'STUDENT') return 'Law college';
  if (track === 'SENIOR_ADVOCATE') return 'Senior office / chamber';
  if (track === 'AOR_CHAMBER_LEAD') return 'Chamber / AOR office';
  if (track === 'JUNIOR_CLERK') return 'Senior / chamber';
  if (track === 'FIRM_LAWYER') return 'Firm / disputes team';
  if (track === 'CORPORATE_COUNSEL') return 'Company / legal team';
  return 'Practice / office name';
}

function getInstitutionPlaceholder(track) {
  if (track === 'STUDENT') return 'e.g. NLU Delhi';
  if (track === 'SENIOR_ADVOCATE') return 'e.g. Senior Counsel Chambers';
  if (track === 'AOR_CHAMBER_LEAD') return 'e.g. Supreme Court Filing Chamber';
  if (track === 'JUNIOR_CLERK') return 'e.g. Sharma Chambers';
  if (track === 'FIRM_LAWYER') return 'e.g. Khaitan & Co';
  if (track === 'CORPORATE_COUNSEL') return 'e.g. Advoq Legal';
  return 'Optional';
}

export default function OnboardingPage() {
  const navigate = useNavigate();
  const { user, setUser, getAuthHeaders } = useAuth();
  const [step, setStep] = useState(1);
  const [practiceTrack, setPracticeTrack] = useState('INDEPENDENT_LITIGATOR');
  const [starterWorkflow, setStarterWorkflow] = useState('');
  const [starterGoal, setStarterGoal] = useState('');
  const [institution, setInstitution] = useState('');
  const [barNumber, setBarNumber] = useState('');
  const [yearOfStudy, setYearOfStudy] = useState(3);
  const [practiceAreas, setPracticeAreas] = useState([]);
  const [courts, setCourts] = useState([]);
  const [language, setLanguage] = useState('ENGLISH');
  const [phone, setPhone] = useState('');
  const [whatsappOptIn, setWhatsappOptIn] = useState(false);
  const [loading, setLoading] = useState(false);

  const starterWorkflows = useMemo(() => buildStarterWorkflows(practiceTrack), [practiceTrack]);
  const selectedTrack = getPracticeTrackMeta(practiceTrack);
  const selectedWorkflow = starterWorkflows.find((workflow) => workflow.workflow_id === starterWorkflow) || starterWorkflows[0];

  useEffect(() => {
    setStarterWorkflow((current) => (
      starterWorkflows.some((workflow) => workflow.workflow_id === current)
        ? current
        : starterWorkflows[0]?.workflow_id || ''
    ));
  }, [starterWorkflows]);

  const handleFinish = async () => {
    setLoading(true);
    try {
      const resp = await fetch(`${API}/onboarding`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          role: selectedTrack.role,
          practice_track: practiceTrack,
          institution,
          bar_number: barNumber,
          year_of_study: practiceTrack === 'STUDENT' ? yearOfStudy : null,
          practice_areas: practiceAreas,
          preferred_courts: courts,
          language,
          phone,
          whatsapp_opt_in: whatsappOptIn,
          starter_workflow: selectedWorkflow?.workflow_id || null,
          starter_goal: starterGoal,
        }),
      });
      if (!resp.ok) throw new Error('Failed to save onboarding');
      const updated = await resp.json();
      setUser(updated);
      toast.success('Your first session is ready.');
      navigate(buildStarterLaunchPath(selectedWorkflow, starterGoal), { replace: true });
    } catch (error) {
      toast.error(error.message || 'Failed to save onboarding');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-8" style={{ background: 'var(--bg-base)' }}>
      <div className="w-full max-w-4xl">
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            {[1, 2, 3, 4].map((value) => (
              <div
                key={value}
                className="flex-1 h-1 rounded-full transition-all duration-300"
                style={{ background: value <= step ? 'var(--accent-primary)' : 'var(--border-subtle)' }}
              />
            ))}
          </div>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <p className="text-xs uppercase tracking-[0.18em]" style={{ color: 'var(--text-muted)' }}>Advoq setup</p>
              <h1 className="text-2xl sm:text-3xl font-bold mt-2" style={{ fontFamily: 'var(--font-display)' }}>
                Shape Advoq into your daily legal operating system.
              </h1>
            </div>
            <div className="advoq-card px-4 py-3">
              <p className="text-xs uppercase tracking-[0.18em]" style={{ color: 'var(--text-muted)' }}>Workflow-first</p>
              <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
                We will keep this lightweight, then drop you into the strongest operating move for your role.
              </p>
            </div>
          </div>
        </div>

        <div className="advoq-card p-6 sm:p-8">
          {step === 1 && (
            <div className="animate-fade-in">
              <span className="text-xs uppercase tracking-[0.18em]" style={{ color: 'var(--text-muted)' }}>Step 1</span>
              <h2 className="text-2xl sm:text-3xl font-bold mt-2 mb-2" style={{ fontFamily: 'var(--font-display)' }}>
                Choose the role Advoq should optimize around.
              </h2>
              <p className="text-sm mb-6" style={{ color: 'var(--text-secondary)' }}>
                This shapes your launch board, return loops, tone, and the workflows that stay closest to the surface every day.
              </p>

              <div className="grid gap-3 sm:grid-cols-2">
                {PRACTICE_TRACK_OPTIONS.map((track) => {
                  const Icon = TRACK_ICONS[track.value] || Scale;
                  const active = practiceTrack === track.value;
                  return (
                    <button
                      key={track.value}
                      type="button"
                      onClick={() => setPracticeTrack(track.value)}
                      className="rounded-2xl p-5 text-left transition-all duration-150"
                      style={{
                        background: active ? 'var(--accent-glow)' : 'var(--bg-surface)',
                        border: `1px solid ${active ? 'var(--accent-primary)' : 'var(--border-subtle)'}`,
                      }}
                    >
                      <div className="flex items-start gap-4">
                        <span
                          className="inline-flex items-center justify-center rounded-2xl"
                          style={{
                            width: '44px',
                            height: '44px',
                            background: active ? 'rgba(15,118,110,0.14)' : 'var(--bg-elevated)',
                            color: active ? 'var(--accent-primary)' : 'var(--text-secondary)',
                          }}
                        >
                          <Icon size={20} />
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-semibold text-sm">{track.label}</p>
                            {active ? <Check size={16} style={{ color: 'var(--accent-primary)' }} /> : null}
                          </div>
                          <p className="text-xs mt-2 leading-6" style={{ color: 'var(--text-muted)' }}>{track.detail}</p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="animate-fade-in">
              <span className="text-xs uppercase tracking-[0.18em]" style={{ color: 'var(--text-muted)' }}>Step 2</span>
              <h2 className="text-2xl sm:text-3xl font-bold mt-2 mb-2" style={{ fontFamily: 'var(--font-display)' }}>
                Pick the first operating move you want today.
              </h2>
              <p className="text-sm mb-6" style={{ color: 'var(--text-secondary)' }}>
                This becomes your launch point right after setup, so you land in a real workflow instead of a blank screen.
              </p>

              <div className="grid gap-3 lg:grid-cols-2">
                {starterWorkflows.map((workflow) => {
                  const active = starterWorkflow === workflow.workflow_id;
                  const Icon = workflow.kind === 'JOLLY' ? Mic : workflow.mode === 'DRAFT' ? Sparkles : MessageSquareText;
                  return (
                    <button
                      key={workflow.workflow_id}
                      type="button"
                      onClick={() => setStarterWorkflow(workflow.workflow_id)}
                      className="rounded-2xl p-5 text-left transition-all duration-150"
                      style={{
                        background: active ? 'var(--accent-glow)' : 'var(--bg-surface)',
                        border: `1px solid ${active ? 'var(--accent-primary)' : 'var(--border-subtle)'}`,
                      }}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <span
                          className="inline-flex items-center justify-center rounded-2xl"
                          style={{
                            width: '42px',
                            height: '42px',
                            background: active ? 'rgba(15,118,110,0.14)' : 'var(--bg-elevated)',
                            color: active ? 'var(--accent-primary)' : 'var(--text-secondary)',
                          }}
                        >
                          <Icon size={18} />
                        </span>
                        <span className="text-[11px] uppercase tracking-[0.18em]" style={{ color: active ? 'var(--accent-primary)' : 'var(--text-muted)' }}>
                          {workflow.kind === 'JOLLY' ? 'Voice' : workflow.mode?.replace(/_/g, ' ')}
                        </span>
                      </div>
                      <h3 className="font-semibold text-sm mt-4">{workflow.label}</h3>
                      <p className="text-xs mt-2 leading-6" style={{ color: 'var(--text-muted)' }}>{workflow.description}</p>
                    </button>
                  );
                })}
              </div>

              <div className="mt-6">
                <label className="text-xs font-medium mb-2 block" style={{ color: 'var(--text-secondary)' }}>
                  Optional day-one goal
                </label>
                <textarea
                  value={starterGoal}
                  onChange={(event) => setStarterGoal(event.target.value)}
                  rows={3}
                  className="advoq-input w-full"
                  placeholder="e.g. Prepare tomorrow's interim injunction hearing brief and client update"
                />
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="animate-fade-in">
              <span className="text-xs uppercase tracking-[0.18em]" style={{ color: 'var(--text-muted)' }}>Step 3</span>
              <h2 className="text-2xl sm:text-3xl font-bold mt-2 mb-2" style={{ fontFamily: 'var(--font-display)' }}>
                Add just enough context to stay useful.
              </h2>
              <p className="text-sm mb-6" style={{ color: 'var(--text-secondary)' }}>
                This helps Advoq shape matter context, continuity, and team cues without slowing you down.
              </p>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="text-xs font-medium mb-2 block" style={{ color: 'var(--text-secondary)' }}>
                    {getInstitutionLabel(practiceTrack)}
                  </label>
                  <input
                    value={institution}
                    onChange={(event) => setInstitution(event.target.value)}
                    className="advoq-input w-full"
                    placeholder={getInstitutionPlaceholder(practiceTrack)}
                  />
                </div>

                {practiceTrack === 'STUDENT' ? (
                  <div>
                    <label className="text-xs font-medium mb-2 block" style={{ color: 'var(--text-secondary)' }}>Year of study</label>
                    <div className="flex gap-2">
                      {[1, 2, 3, 4, 5].map((year) => (
                        <button
                          key={year}
                          type="button"
                          onClick={() => setYearOfStudy(year)}
                          className="flex-1 py-2 rounded-lg text-sm font-medium transition-all duration-150"
                          style={{
                            background: yearOfStudy === year ? 'var(--accent-primary)' : 'var(--bg-elevated)',
                            color: yearOfStudy === year ? 'var(--text-inverse)' : 'var(--text-secondary)',
                            border: `1px solid ${yearOfStudy === year ? 'var(--accent-primary)' : 'var(--border-subtle)'}`,
                          }}
                        >
                          {year}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div>
                    <label className="text-xs font-medium mb-2 block" style={{ color: 'var(--text-secondary)' }}>Bar council number</label>
                    <input
                      value={barNumber}
                      onChange={(event) => setBarNumber(event.target.value)}
                      className="advoq-input w-full"
                      placeholder="Optional"
                    />
                  </div>
                )}
              </div>

              <div className="mt-6">
                <label className="text-xs font-medium mb-2 block" style={{ color: 'var(--text-secondary)' }}>Practice focus</label>
                <div className="flex flex-wrap gap-2">
                  {PRACTICE_AREAS.map((area) => (
                    <button
                      key={area}
                      type="button"
                      onClick={() => setPracticeAreas((prev) => toggleItem(prev, area))}
                      className="px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-150"
                      style={{
                        background: practiceAreas.includes(area) ? 'var(--accent-glow)' : 'var(--bg-elevated)',
                        color: practiceAreas.includes(area) ? 'var(--accent-primary)' : 'var(--text-secondary)',
                        border: `1px solid ${practiceAreas.includes(area) ? 'var(--accent-primary)' : 'var(--border-subtle)'}`,
                      }}
                    >
                      {area}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-6">
                <label className="text-xs font-medium mb-2 block" style={{ color: 'var(--text-secondary)' }}>Courts you touch most</label>
                <div className="flex flex-wrap gap-2">
                  {COURTS.map((court) => (
                    <button
                      key={court}
                      type="button"
                      onClick={() => setCourts((prev) => toggleItem(prev, court))}
                      className="px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-150"
                      style={{
                        background: courts.includes(court) ? 'var(--accent-glow)' : 'var(--bg-elevated)',
                        color: courts.includes(court) ? 'var(--accent-primary)' : 'var(--text-secondary)',
                        border: `1px solid ${courts.includes(court) ? 'var(--accent-primary)' : 'var(--border-subtle)'}`,
                      }}
                    >
                      {court}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="animate-fade-in">
              <span className="text-xs uppercase tracking-[0.18em]" style={{ color: 'var(--text-muted)' }}>Step 4</span>
              <h2 className="text-2xl sm:text-3xl font-bold mt-2 mb-2" style={{ fontFamily: 'var(--font-display)' }}>
                Confirm how you want to work.
              </h2>
              <p className="text-sm mb-6" style={{ color: 'var(--text-secondary)' }}>
                Multilingual work stays supported across files, voice capture, and translation. Your daily return loop stays lightweight and matter-aware.
              </p>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {LANGUAGES.map((item) => (
                  <button
                    key={item.value}
                    type="button"
                    onClick={() => setLanguage(item.value)}
                    className="flex items-center gap-3 p-4 rounded-xl transition-all duration-150"
                    style={{
                      background: language === item.value ? 'var(--accent-glow)' : 'var(--bg-surface)',
                      border: `1px solid ${language === item.value ? 'var(--accent-primary)' : 'var(--border-subtle)'}`,
                    }}
                  >
                    <span
                      className="text-sm font-bold"
                      style={{ color: language === item.value ? 'var(--accent-primary)' : 'var(--text-muted)', fontFamily: "'JetBrains Mono', monospace" }}
                    >
                      {item.code}
                    </span>
                    <span className="text-sm font-medium">{item.label}</span>
                  </button>
                ))}
              </div>

              <div className="grid gap-4 sm:grid-cols-[1.1fr_0.9fr] mt-6">
                <div className="advoq-card p-5">
                  <label className="text-xs font-medium mb-2 block" style={{ color: 'var(--text-secondary)' }}>
                    WhatsApp for daily return loop
                  </label>
                  <div className="flex gap-2">
                    <span className="advoq-input flex items-center px-3 text-sm" style={{ color: 'var(--text-muted)' }}>+91</span>
                    <input
                      type="tel"
                      value={phone}
                      onChange={(event) => setPhone(event.target.value)}
                      className="advoq-input flex-1"
                      placeholder="Optional"
                    />
                  </div>
                  <label className="flex items-start gap-3 mt-4 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={whatsappOptIn}
                      onChange={(event) => setWhatsappOptIn(event.target.checked)}
                      className="mt-1 w-4 h-4 rounded"
                      style={{ accentColor: 'var(--accent-primary)' }}
                    />
                    <span className="text-sm leading-6" style={{ color: 'var(--text-secondary)' }}>
                      Send me a lightweight daily update so I can reopen Advoq straight into active legal work.
                    </span>
                  </label>
                </div>

                <div className="advoq-card p-5" data-testid="onboarding-launch-preview">
                  <p className="text-xs uppercase tracking-[0.18em]" style={{ color: 'var(--text-muted)' }}>You will launch into</p>
                  <h3 className="text-lg font-semibold mt-2" style={{ fontFamily: 'var(--font-display)' }}>{selectedWorkflow?.label}</h3>
                  <p className="text-sm leading-6 mt-3" style={{ color: 'var(--text-secondary)' }}>{selectedWorkflow?.description}</p>
                  {starterGoal ? (
                    <div className="mt-4 rounded-xl px-3 py-3" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}>
                      <p className="text-[11px] uppercase tracking-[0.18em]" style={{ color: 'var(--text-muted)' }}>Specific goal</p>
                      <p className="text-sm mt-2" style={{ color: 'var(--text-primary)' }}>{starterGoal}</p>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between mt-8">
            {step > 1 ? (
              <button type="button" onClick={() => setStep((value) => value - 1)} className="advoq-btn-ghost flex items-center gap-2">
                <ArrowLeft size={16} /> Back
              </button>
            ) : <div />}

            {step < 4 ? (
              <button type="button" onClick={() => setStep((value) => value + 1)} className="advoq-btn-primary flex items-center gap-2">
                Next <ArrowRight size={16} />
              </button>
            ) : (
              <button
                type="button"
                onClick={handleFinish}
                disabled={loading}
                className="advoq-btn-primary flex items-center gap-2"
              >
                {loading ? 'Starting your workspace…' : `Launch ${user?.name?.split(' ')[0] || selectedTrack.shortLabel}'s first win`}
                {!loading ? <ArrowRight size={16} /> : null}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
